import { fetchWithAuth } from "../api/client";
import { User, ErrorResponse, EmptyResponse } from "../types";

export async function getUsers(token: string): Promise<User[]> {
  const res = await fetchWithAuth("User", {
    method: "GET",
    token,
  });

  if (!res.ok) {
    throw new Error(`Error fetching users: ${res.statusText}`);
  }

  return await res.json();
}
type UserServer = Omit<User, "status"> & {
  status: number;
};
export async function getUserById(
  id: string,
  token: string
): Promise<UserServer | ErrorResponse> {
  const res = await fetchWithAuth(`User/${id}`, {
    method: "GET",
    token,
  });
  if (!res.ok) {
    return {
      error: res.status,
      message: `Error fetching user: ${res.statusText}`,
    };
  }
  return await res.json();
}
type CreateUserPayload = Omit<User, "id" | "createdAt" | "status" | "role"> & {
  confirmPassword: string;
  status: number;
  roleId: string | undefined;
};

export async function createUser(
  data: Omit<User, "id" | "createdAt">,
  token: string
): Promise<User | ErrorResponse> {
  let payload: CreateUserPayload = {
    fullName: data.fullName,
    email: data.email,
    password: data.password,
    confirmPassword: data.password ?? "",
    roleId: data.roleId,
    status: data.status,
  };
  if (data.role === "Organization") {
    payload = {
      ...payload,
      organizationId: data.organizationId,
      organization: data.organization,
    };
  }

  try {
    const res = await fetchWithAuth("User", {
      method: "POST",
      token,
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      return {
        error: res.status,
        message: `Error creating user: ${res.statusText}`,
      };
    }
    return await res.json();
  } catch (err: any) {
    return {
      error: 500,
      message: err.message,
    };
  }
}

export async function updateUser(
  id: string,
  data: Omit<User, "createdAt">,
  token: string
): Promise<EmptyResponse | ErrorResponse> {
  type UpdateUserPayload = Omit<
    CreateUserPayload,
    "password" | "confirmPassword"
  > & { id: string; Password?: string };

  let payload: UpdateUserPayload = {
    fullName: data.fullName,
    email: data.email,
    roleId: data.roleId,
    status: data.status,
    id: id,
  };
  if (data.password && data.password.trim().length > 0) {
    payload.Password = data.password;
  }
  if (data.role === "Organization") {
    payload = {
      ...payload,
      organizationId: data.organizationId,
      organization: data.organization,
    };
  }

  const res = await fetchWithAuth(`User/${id}`, {
    method: "PUT",
    token,
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    return {
      error: res.status,
      message: `Error updating user: ${res.statusText}`,
    };
  }

  const text = await res.text();
  return { ok: res.ok, message: text || "Entity updated successfully" };
}

export async function deleteUser(userToDelete: User, token: string) {
  const res = await fetchWithAuth(`User/${userToDelete.id}`, {
    method: "DELETE",
    token,
  });

  if (!res.ok) {
    return {
      error: res.status,
      message: `Error deleting user: ${res.statusText}`,
    };
  }

  return { ok: true };
}
